// Vocal / beat clip tools — touch sliders for Gain + smooth Fades.
// Dragging previews live (visual + audible); release saves. Non-destructive.
import React, { useEffect, useRef } from "react";
import { X, Undo2, Redo2 } from "lucide-react";
import { Slider } from "../ui/slider";
import { fadeShape } from "../../lib/studioEngine";

const fmtDb = (v) => `${v > 0 ? "+" : ""}${v.toFixed(1)} dB`;
const fmtSec = (ms) => (ms <= 0 ? "Off" : `${(ms / 1000).toFixed(2)} s`);

function FadeCurve({ dir, ms, max }) {
  const frac = max > 0 ? Math.min(1, ms / max) : 0;
  const pts = [];
  for (let i = 0; i <= 24; i++) {
    const x = i / 24;
    let y = 1;
    if (dir === "in" && x < frac) y = fadeShape(x / frac);
    if (dir === "out" && x > 1 - frac) y = fadeShape((1 - x) / frac);
    pts.push(`${(x * 64).toFixed(1)},${(22 - y * 20).toFixed(1)}`);
  }
  return (
    <svg viewBox="0 0 64 24" className="w-16 h-6 flex-shrink-0" data-testid={`fade-${dir}-curve`}>
      <polyline points={pts.join(" ")} fill="none" stroke="currentColor" strokeWidth="1.6" className="text-fuchsia-300" />
    </svg>
  );
}

function SliderRow({ label, value, testid, children }) {
  return (
    <div className="space-y-2" data-testid={`${testid}-row`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">{label}</span>
        <span className="text-xs tabular-nums text-white font-bold" data-testid={`${testid}-value`}>{value}</span>
      </div>
      {children}
    </div>
  );
}

const sliderCls = "py-3 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:bg-white [&_[role=slider]]:border-fuchsia-400 [&>span:first-child]:h-2 [&>span:first-child]:bg-white/10 [&>span:first-child>span]:bg-fuchsia-500";

export default function ClipToolsDrawer({
  open, onClose, kind, gainDb = 0, fadeInMs = 0, fadeOutMs = 0, maxFadeMs = 10000,
  onPreview, onCommit, onNormalize, onNudge, onUndo, onRedo, canUndo, canRedo, undoLabel, redoLabel,
}) {
  // Keyboard arrows commit on every step — batch them into one save/undo.
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const commit = (partial, label) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit?.(partial, label), 400);
  };
  if (!open) return null;
  const maxFade = Math.max(100, Math.min(10000, Math.floor(maxFadeMs / 50) * 50));
  return (
    <aside
      className="fixed inset-x-0 bottom-0 z-50 bg-[#0f0b1a] border-t border-fuchsia-500/30 rounded-t-2xl shadow-[0_-8px_24px_rgba(0,0,0,0.55)] pb-6 max-h-[60vh] overflow-y-auto"
      data-testid="clip-tools-drawer" role="dialog" aria-label="Clip tools"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <p className="text-[11px] uppercase tracking-widest font-bold text-fuchsia-300">
          {kind === "beat" ? "Beat clip tools" : "Vocal tools"}
        </p>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="clip-tools-close-btn" aria-label="Close tools">
          <X className="w-4 h-4" />
        </button>
      </header>
      <div className="px-5 pt-4 space-y-5 max-w-xl mx-auto">
        <SliderRow label="Gain" value={fmtDb(gainDb)} testid="clip-gain">
          <Slider min={-24} max={12} step={0.5} value={[gainDb]} className={sliderCls}
            onValueChange={([v]) => onPreview?.({ clip_gain_db: v })}
            onValueCommit={([v]) => commit({ clip_gain_db: v }, "gain")}
            data-testid="clip-gain-slider" aria-label="Clip gain" />
        </SliderRow>
        <SliderRow label="Fade in" value={fmtSec(fadeInMs)} testid="clip-fade-in">
          <div className="flex items-center gap-3">
            <FadeCurve dir="in" ms={fadeInMs} max={maxFade} />
            <Slider min={0} max={maxFade} step={50} value={[Math.min(fadeInMs, maxFade)]} className={sliderCls}
              onValueChange={([v]) => onPreview?.({ fade_in_ms: v })}
              onValueCommit={([v]) => commit({ fade_in_ms: v }, "fade in")}
              data-testid="clip-fade-in-slider" aria-label="Fade in" />
          </div>
        </SliderRow>
        <SliderRow label="Fade out" value={fmtSec(fadeOutMs)} testid="clip-fade-out">
          <div className="flex items-center gap-3">
            <FadeCurve dir="out" ms={fadeOutMs} max={maxFade} />
            <Slider min={0} max={maxFade} step={50} value={[Math.min(fadeOutMs, maxFade)]} className={sliderCls}
              onValueChange={([v]) => onPreview?.({ fade_out_ms: v })}
              onValueCommit={([v]) => commit({ fade_out_ms: v }, "fade out")}
              data-testid="clip-fade-out-slider" aria-label="Fade out" />
          </div>
        </SliderRow>
        <div className="flex flex-wrap items-center gap-2" data-testid="clip-tools-move-row">
          <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold w-14">Nudge</span>
          {[[-1000, "← 1s", "clip-nudge-left-btn"], [-100, "← 0.1s", "clip-nudge-left-small-btn"],
            [100, "0.1s →", "clip-nudge-right-small-btn"], [1000, "1s →", "clip-nudge-right-btn"]].map(([d, l, id]) => (
            <Btn key={id} onClick={() => onNudge?.(d)} testid={id}>{l}</Btn>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onNormalize && <Btn onClick={onNormalize} testid="clip-normalize-btn">Normalize</Btn>}
          <Btn onClick={onUndo} disabled={!canUndo} testid="clip-undo-btn"><Undo2 className="w-3.5 h-3.5 inline mr-1" />Undo{undoLabel ? ` · ${undoLabel}` : ""}</Btn>
          <Btn onClick={onRedo} disabled={!canRedo} testid="clip-redo-btn"><Redo2 className="w-3.5 h-3.5 inline mr-1" />Redo{redoLabel ? ` · ${redoLabel}` : ""}</Btn>
        </div>
      </div>
    </aside>
  );
}

function Btn({ onClick, testid, disabled, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`px-3 py-2 rounded-full text-[11px] font-bold border whitespace-nowrap transition-colors ${
        disabled ? "border-white/5 text-gray-600 cursor-not-allowed" : "border-white/15 text-gray-200 hover:border-fuchsia-400/60 hover:bg-white/5"}`}
    >{children}</button>
  );
}
